/**
 * Shape Utility Functions.
 *
 * Pure geometry functions for computing preset shape vertices, bounding
 * boxes, point-in-polygon hit testing, and alignment snap calculations.
 * Used by the interactive yard map canvas editor.
 */

export type ShapeType =
  | "rectangle"
  | "circle"
  | "l_shape"
  | "t_shape"
  | "triangle"
  | "polygon";

export interface Point {
  x: number;
  y: number;
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

// ---------------------------------------------------------------------------
// Preset Shape Vertex Generation
// ---------------------------------------------------------------------------

/**
 * Compute vertices for a preset shape from its bounding box.
 * Returns an array of {x, y} points in clockwise order.
 * For rectangles and circles, returns the bounding corners.
 */
export function getShapeVertices(
  shapeType: ShapeType,
  x: number,
  y: number,
  width: number,
  height: number,
): Point[] {
  switch (shapeType) {
    case "rectangle":
      return [
        { x, y },
        { x: x + width, y },
        { x: x + width, y: y + height },
        { x, y: y + height },
      ];

    case "triangle":
      return [
        { x: x + width / 2, y }, // top center
        { x: x + width, y: y + height }, // bottom right
        { x, y: y + height }, // bottom left
      ];

    case "l_shape": {
      // L-shape: full width bottom, half width top-left
      const midX = x + width * 0.45;
      const midY = y + height * 0.55;
      return [
        { x, y },
        { x: midX, y },
        { x: midX, y: midY },
        { x: x + width, y: midY },
        { x: x + width, y: y + height },
        { x, y: y + height },
      ];
    }

    case "t_shape": {
      // T-shape: full width top bar, narrow stem down center
      const stemLeft = x + width * 0.3;
      const stemRight = x + width * 0.7;
      const barBottom = y + height * 0.35;
      return [
        { x, y },
        { x: x + width, y },
        { x: x + width, y: barBottom },
        { x: stemRight, y: barBottom },
        { x: stemRight, y: y + height },
        { x: stemLeft, y: y + height },
        { x: stemLeft, y: barBottom },
        { x, y: barBottom },
      ];
    }

    case "circle":
      // Return bounding box corners for hit testing; actual rendering uses <circle>
      return [
        { x, y },
        { x: x + width, y },
        { x: x + width, y: y + height },
        { x, y: y + height },
      ];

    case "polygon":
      // Custom polygons store their own points; this is a fallback
      return [
        { x, y },
        { x: x + width, y },
        { x: x + width, y: y + height },
        { x, y: y + height },
      ];

    default:
      return [
        { x, y },
        { x: x + width, y },
        { x: x + width, y: y + height },
        { x, y: y + height },
      ];
  }
}

/**
 * Convert a points array to an SVG polygon points string.
 */
export function pointsToSvgString(pts: Point[]): string {
  return pts.map((p) => `${p.x},${p.y}`).join(" ");
}

// ---------------------------------------------------------------------------
// Real-world measurement (map units == real-world units of the map's `unit`)
// ---------------------------------------------------------------------------

/** Euclidean length of the segment between two points, in map units. */
export function segmentLength(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Total perimeter of a closed polygon, in map units. */
export function polygonPerimeter(pts: Point[]): number {
  if (pts.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < pts.length; i++) {
    total += segmentLength(pts[i]!, pts[(i + 1) % pts.length]!);
  }
  return total;
}

/**
 * Format a length (in map units) with its unit label. Values >= 100 are
 * rounded to whole units; smaller values keep one decimal for precision.
 */
export function formatLength(value: number, unit: string): string {
  const rounded =
    value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${unit}`;
}

/**
 * Move `current` along the ray from `anchor` so the segment anchor→current
 * has exactly `newLength`. Direction is preserved; a zero-length segment is
 * returned unchanged (no direction to scale along).
 */
export function movePointAlongSegment(
  anchor: Point,
  current: Point,
  newLength: number,
): Point {
  const len = segmentLength(anchor, current);
  if (len === 0) return { ...current };
  const t = newLength / len;
  return {
    x: anchor.x + (current.x - anchor.x) * t,
    y: anchor.y + (current.y - anchor.y) * t,
  };
}

/**
 * Constrain `p` to a horizontal or vertical line from `anchor` (whichever axis
 * the pointer is further along) — the "ortho lock" used while drawing when
 * Shift is held, so segments stay axis-aligned.
 */
export function orthoConstrain(anchor: Point, p: Point): Point {
  return Math.abs(p.x - anchor.x) >= Math.abs(p.y - anchor.y)
    ? { x: p.x, y: anchor.y }
    : { x: anchor.x, y: p.y };
}

/** Unit direction from `a` to `b`; {0,0} if coincident. */
export function unitVector(a: Point, b: Point): Point {
  const len = segmentLength(a, b);
  if (len === 0) return { x: 0, y: 0 };
  return { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
}

/** Signed area of a polygon (shoelace); sign encodes winding order. */
export function polygonSignedArea(pts: Point[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!;
    const q = pts[(i + 1) % pts.length]!;
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}

/** Absolute area of a closed polygon, in map units². */
export function polygonArea(pts: Point[]): number {
  return Math.abs(polygonSignedArea(pts));
}

/** Shortest distance from point `p` to segment `a`→`b`. */
export function distancePointToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Closest point on segment `a`→`b` to `p`. */
export function nearestPointOnSegment(p: Point, a: Point, b: Point): Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * dx, y: a.y + t * dy };
}

/** Closest point on a polygon's edges to `p`, with its distance. */
export function nearestPointOnPolygon(
  p: Point,
  poly: Point[],
): { point: Point; dist: number } {
  let best = { point: poly[0] ?? p, dist: Infinity };
  for (let i = 0; i < poly.length; i++) {
    const q = nearestPointOnSegment(p, poly[i]!, poly[(i + 1) % poly.length]!);
    const d = Math.hypot(p.x - q.x, p.y - q.y);
    if (d < best.dist) best = { point: q, dist: d };
  }
  return best;
}

/** Shortest distance from a point to any edge of a polygon. */
export function distancePointToPolygon(p: Point, poly: Point[]): number {
  let min = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const d = distancePointToSegment(p, poly[i]!, poly[(i + 1) % poly.length]!);
    if (d < min) min = d;
  }
  return min;
}

/** Whether segments p1→p2 and p3→p4 properly cross. */
export function segmentsIntersect(
  p1: Point,
  p2: Point,
  p3: Point,
  p4: Point,
): boolean {
  const orient = (a: Point, b: Point, c: Point) =>
    (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const d1 = orient(p3, p4, p1);
  const d2 = orient(p3, p4, p2);
  const d3 = orient(p1, p2, p3);
  const d4 = orient(p1, p2, p4);
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
}

/** Whether two polygons overlap (edge crossing or containment). */
export function polygonsOverlap(a: Point[], b: Point[]): boolean {
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (
        segmentsIntersect(
          a[i]!,
          a[(i + 1) % a.length]!,
          b[j]!,
          b[(j + 1) % b.length]!,
        )
      )
        return true;
    }
  }
  if (a[0] && pointInPolygon(a[0].x, a[0].y, b)) return true;
  if (b[0] && pointInPolygon(b[0].x, b[0].y, a)) return true;
  return false;
}

/** Whether `inner` lies entirely within `outer` (no crossings, all inside). */
export function polygonWithin(inner: Point[], outer: Point[]): boolean {
  for (const p of inner) {
    if (!pointInPolygon(p.x, p.y, outer)) return false;
  }
  for (let i = 0; i < inner.length; i++) {
    for (let j = 0; j < outer.length; j++) {
      if (
        segmentsIntersect(
          inner[i]!,
          inner[(i + 1) % inner.length]!,
          outer[j]!,
          outer[(j + 1) % outer.length]!,
        )
      )
        return false;
    }
  }
  return true;
}

/** Minimum gap between two polygons (0 if they overlap). */
export function shapeClearance(a: Point[], b: Point[]): number {
  if (polygonsOverlap(a, b)) return 0;
  let min = Infinity;
  for (const p of a) min = Math.min(min, distancePointToPolygon(p, b));
  for (const p of b) min = Math.min(min, distancePointToPolygon(p, a));
  return min;
}

/** Intersection point of two infinite lines (point + direction); null if parallel. */
function lineIntersect(
  p1: Point,
  d1: Point,
  p2: Point,
  d2: Point,
): Point | null {
  const denom = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / denom;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
}

/** Offset every edge of a polygon by `dist`, then re-intersect (miter join). */
function offsetWithSign(
  pts: Point[],
  dist: number,
  sign: 1 | -1,
): Point[] | null {
  const n = pts.length;
  const lines: Array<{ p: Point; d: Point }> = [];
  for (let i = 0; i < n; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % n]!;
    let ex = b.x - a.x;
    let ey = b.y - a.y;
    const len = Math.hypot(ex, ey);
    if (len < 1e-9) return null;
    ex /= len;
    ey /= len;
    const nx = -ey * sign;
    const ny = ex * sign;
    lines.push({
      p: { x: a.x + nx * dist, y: a.y + ny * dist },
      d: { x: ex, y: ey },
    });
  }
  const out: Point[] = [];
  for (let i = 0; i < n; i++) {
    const prev = lines[(i - 1 + n) % n]!;
    const cur = lines[i]!;
    const ip = lineIntersect(prev.p, prev.d, cur.p, cur.d);
    if (!ip) return null;
    out.push(ip);
  }
  return out;
}

/**
 * Inward offset (setback) of a closed polygon by `dist` map units. Tries both
 * normal directions and keeps the one that lands inside the original; returns
 * `null` if the offset self-collapses (setback too large for the shape) — the
 * caller should then skip rendering the setback line.
 */
export function offsetPolygon(pts: Point[], dist: number): Point[] | null {
  if (pts.length < 3 || dist <= 0) return null;
  for (const sign of [1, -1] as const) {
    const res = offsetWithSign(pts, dist, sign);
    if (
      res &&
      res.every((p) => pointInPolygon(p.x, p.y, pts)) &&
      polygonArea(res) >= 1
    ) {
      return res;
    }
  }
  return null;
}

/**
 * Choose a "nice" round tick interval (1/2/5 x 10^n) that yields roughly
 * `targetTicks` divisions across `span`. Keeps rulers legible whether the map
 * is 30 ft or 3000 ft.
 */
export function niceTickInterval(span: number, targetTicks = 8): number {
  if (span <= 0) return 1;
  const rough = span / targetTicks;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / pow;
  let nice: number;
  if (norm < 1.5) nice = 1;
  else if (norm < 3) nice = 2;
  else if (norm < 7) nice = 5;
  else nice = 10;
  return nice * pow;
}

// ---------------------------------------------------------------------------
// Bounding Box
// ---------------------------------------------------------------------------

/**
 * Compute the axis-aligned bounding box for an array of points.
 */
export function getBoundingBox(pts: Point[]): BoundingBox {
  if (pts.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

// ---------------------------------------------------------------------------
// Hit Testing
// ---------------------------------------------------------------------------

/**
 * Point-in-polygon test using the ray casting algorithm.
 */
export function pointInPolygon(
  px: number,
  py: number,
  polygon: Point[],
): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i]!.x,
      yi = polygon[i]!.y;
    const xj = polygon[j]!.x,
      yj = polygon[j]!.y;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Point-in-circle test.
 */
export function pointInCircle(
  px: number,
  py: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): boolean {
  return (px - cx) ** 2 / rx ** 2 + (py - cy) ** 2 / ry ** 2 <= 1;
}

/**
 * Hit test for any shape type.
 */
export function hitTestSection(
  px: number,
  py: number,
  shapeType: ShapeType,
  x: number,
  y: number,
  width: number,
  height: number,
  points?: Point[] | null,
): boolean {
  if (shapeType === "circle") {
    return pointInCircle(
      px,
      py,
      x + width / 2,
      y + height / 2,
      width / 2,
      height / 2,
    );
  }
  if (shapeType === "polygon" && points && points.length >= 3) {
    return pointInPolygon(px, py, points);
  }
  // For all preset polygon shapes, compute vertices and test
  const verts = getShapeVertices(shapeType, x, y, width, height);
  return pointInPolygon(px, py, verts);
}

// ---------------------------------------------------------------------------
// Alignment / Snap
// ---------------------------------------------------------------------------

export interface SnapGuide {
  axis: "x" | "y";
  position: number;
  type: "edge" | "center";
}

const SNAP_THRESHOLD = 8; // yard units

/**
 * Find alignment snap guides for a section being dragged.
 * Returns guides the section should snap to and the adjusted position.
 */
export function findSnapGuides(
  dragX: number,
  dragY: number,
  dragW: number,
  dragH: number,
  dragId: string,
  otherSections: Array<{
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }>,
  yardWidth: number,
  yardHeight: number,
): { guides: SnapGuide[]; snappedX: number; snappedY: number } {
  const guides: SnapGuide[] = [];
  let snappedX = dragX;
  let snappedY = dragY;

  const dragCX = dragX + dragW / 2;
  const dragCY = dragY + dragH / 2;
  const dragRight = dragX + dragW;
  const dragBottom = dragY + dragH;

  // Collect all candidate positions: other section edges/centers + yard boundaries
  const xCandidates: Array<{ pos: number; type: "edge" | "center" }> = [
    { pos: 0, type: "edge" },
    { pos: yardWidth, type: "edge" },
    { pos: yardWidth / 2, type: "center" },
  ];
  const yCandidates: Array<{ pos: number; type: "edge" | "center" }> = [
    { pos: 0, type: "edge" },
    { pos: yardHeight, type: "edge" },
    { pos: yardHeight / 2, type: "center" },
  ];

  for (const s of otherSections) {
    if (s.id === dragId) continue;
    xCandidates.push(
      { pos: s.x, type: "edge" },
      { pos: s.x + s.width, type: "edge" },
      { pos: s.x + s.width / 2, type: "center" },
    );
    yCandidates.push(
      { pos: s.y, type: "edge" },
      { pos: s.y + s.height, type: "edge" },
      { pos: s.y + s.height / 2, type: "center" },
    );
  }

  // Check x-axis snapping (left edge, right edge, center)
  const dragXEdges = [
    { offset: 0, value: dragX }, // left
    { offset: dragW, value: dragRight }, // right
    { offset: dragW / 2, value: dragCX }, // center
  ];

  let bestXDist = SNAP_THRESHOLD;
  for (const edge of dragXEdges) {
    for (const cand of xCandidates) {
      const dist = Math.abs(edge.value - cand.pos);
      if (dist < bestXDist) {
        bestXDist = dist;
        snappedX = cand.pos - (edge.value - dragX);
        guides.push({ axis: "x", position: cand.pos, type: cand.type });
      }
    }
  }

  // Check y-axis snapping
  const dragYEdges = [
    { offset: 0, value: dragY },
    { offset: dragH, value: dragBottom },
    { offset: dragH / 2, value: dragCY },
  ];

  let bestYDist = SNAP_THRESHOLD;
  for (const edge of dragYEdges) {
    for (const cand of yCandidates) {
      const dist = Math.abs(edge.value - cand.pos);
      if (dist < bestYDist) {
        bestYDist = dist;
        snappedY = cand.pos - (edge.value - dragY);
        guides.push({ axis: "y", position: cand.pos, type: cand.type });
      }
    }
  }

  return { guides, snappedX, snappedY };
}

// ---------------------------------------------------------------------------
// Distance to Boundary
// ---------------------------------------------------------------------------

export interface BoundaryDistance {
  side: "left" | "right" | "top" | "bottom";
  distance: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

/**
 * Compute distances from a section's edges to the yard boundaries.
 */
export function getBoundaryDistances(
  x: number,
  y: number,
  width: number,
  height: number,
  yardWidth: number,
  yardHeight: number,
): BoundaryDistance[] {
  const cx = x + width / 2;
  const cy = y + height / 2;
  return [
    {
      side: "left",
      distance: Math.round(x),
      fromX: x,
      fromY: cy,
      toX: 0,
      toY: cy,
    },
    {
      side: "right",
      distance: Math.round(yardWidth - x - width),
      fromX: x + width,
      fromY: cy,
      toX: yardWidth,
      toY: cy,
    },
    {
      side: "top",
      distance: Math.round(y),
      fromX: cx,
      fromY: y,
      toX: cx,
      toY: 0,
    },
    {
      side: "bottom",
      distance: Math.round(yardHeight - y - height),
      fromX: cx,
      fromY: y + height,
      toX: cx,
      toY: yardHeight,
    },
  ];
}
